// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from 'common/models/flow/ImmutableModel'; // Make sure you copied this file into your repo

import type { SkillIdModelType } from 'common/models/flow/SkillId';
import type { TransporterIdModelType } from 'common/models/flow/TransporterId';
import type { VolumeModelType } from 'common/models/flow/Volume';
import type { DimensionsModelType } from 'common/models/flow/Dimensions';
import type { DoorDimensionsModelType } from './DoorDimensions';
import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';
import type { RiderPersonaLimitsModelType } from './RiderPersonaLimits';

import { SkillId } from 'common/models/internal/SkillId';
import { TransporterId } from 'common/models/internal/TransporterId';
import { Volume } from 'common/models/internal/Volume';
import { Dimensions } from 'common/models/internal/Dimensions';
import { DoorDimensions } from 'common/models/internal/DoorDimensions';
import { AuditMetadata } from 'common/models/internal/AuditMetadata';
import { RiderPersonaLimits } from './RiderPersonaLimits';

export type BagTypeEnum = 'CONTAINER' | 'PALLET';
export type StatusEnum = 'ACTIVE' | 'INACTIVE';
export type LoadingUnloadingSideEnum = 'REAR' | 'RIGHT' | 'LEFT' | 'TOP';
export type TransportModeEnum = 'ROAD' | 'RAIL' | 'SEA' | 'AIR';

export type MasterBagModelModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: StatusEnum,
    description: ?string,
    type: BagTypeEnum,
    skills: ?Array<SkillIdModelType>,
    transporterId: ?TransporterIdModelType,
    volume: VolumeModelType,
    usableVolume: VolumeModelType,
    dimensions: DimensionsModelType,
    usableDimensions: DimensionsModelType,
    pieceDimensionLimit: ?DimensionsModelType,
    doorDimensions: ?DoorDimensionsModelType,
    unladenWeight: ?number,
    maxPayloadWeight: ?number,
    maxStackableWeight: ?number,
    maxStackableCount: ?number,
    limits: ?RiderPersonaLimitsModelType,
    unloadingSide: ?LoadingUnloadingSideEnum,
    transportModes: Array<TransportModeEnum>,
    doorCount: ?number,
    auditMetadata: AuditMetadataModelType,
    customProperties: { [key: string]: string },
    version: number
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterBagModel extends ImmutableModel {
    static fromJS(json: MasterBagModelModelType): MasterBagModel {
        const state: Object = Object.assign({}, json);
        state.skills = state.skills
            ? Immutable.List(state.skills).map(item => SkillId.fromJS(item))
            : state.skills;
        state.transporterId = state.transporterId
            ? TransporterId.fromJS(state.transporterId)
            : state.transporterId;
        state.volume = Volume.fromJS(state.volume);
        state.usableVolume = Volume.fromJS(state.usableVolume);
        state.dimensions = Dimensions.fromJS(state.dimensions);
        state.usableDimensions = Dimensions.fromJS(state.usableDimensions);
        state.pieceDimensionLimit = state.pieceDimensionLimit
            ? Dimensions.fromJS(state.pieceDimensionLimit)
            : state.pieceDimensionLimit;
        state.doorDimensions = state.doorDimensions
            ? DoorDimensions.fromJS(state.doorDimensions)
            : state.doorDimensions;
        state.limits = state.limits
            ? RiderPersonaLimits.fromJS(state.limits)
            : state.limits;
        state.transportModes = Immutable.List(state.transportModes);
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        return new this(Immutable.Map(state));
    }

    toJS(): MasterBagModelModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            description: this.description,
            type: this.type,
            skills: this.skills
                ? this.skills.toArray().map(item => item.toJS())
                : this.skills,
            transporterId: this.transporterId
                ? this.transporterId.toJS()
                : this.transporterId,
            volume: this.volume.toJS(),
            usableVolume: this.usableVolume.toJS(),
            dimensions: this.dimensions.toJS(),
            usableDimensions: this.usableDimensions.toJS(),
            pieceDimensionLimit: this.pieceDimensionLimit
                ? this.pieceDimensionLimit.toJS()
                : this.pieceDimensionLimit,
            doorDimensions: this.doorDimensions
                ? this.doorDimensions.toJS()
                : this.doorDimensions,
            unladenWeight: this.unladenWeight,
            maxPayloadWeight: this.maxPayloadWeight,
            maxStackableWeight: this.maxStackableWeight,
            maxStackableCount: this.maxStackableCount,
            limits: this.limits ? this.limits.toJS() : this.limits,
            unloadingSide: this.unloadingSide,
            transportModes: this.transportModes.toArray(),
            doorCount: this.doorCount,
            auditMetadata: this.auditMetadata.toJS(),
            customProperties: this.customProperties.toObject(),
            version: this.version
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get description(): ?string {
        return this._state.get('description');
    }

    setDescription(description: ?string): this {
        return this.clone(this._state.set('description', description));
    }

    get type(): BagTypeEnum {
        return this._state.get('type');
    }

    setType(type: BagTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get skills(): ?Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    setSkills(skills: ?Immutable.List<SkillId>): this {
        return this.clone(this._state.set('skills', skills));
    }

    get transporterId(): ?TransporterId {
        return this._state.get('transporterId');
    }

    setTransporterId(transporterId: ?TransporterId): this {
        return this.clone(this._state.set('transporterId', transporterId));
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    setVolume(volume: Volume): this {
        return this.clone(this._state.set('volume', volume));
    }

    get usableVolume(): Volume {
        return this._state.get('usableVolume');
    }

    setUsableVolume(usableVolume: Volume): this {
        return this.clone(this._state.set('usableVolume', usableVolume));
    }

    get dimensions(): Dimensions {
        return this._state.get('dimensions');
    }

    setDimensions(dimensions: Dimensions): this {
        return this.clone(this._state.set('dimensions', dimensions));
    }

    get usableDimensions(): Dimensions {
        return this._state.get('usableDimensions');
    }

    setUsableDimensions(usableDimensions: Dimensions): this {
        return this.clone(
            this._state.set('usableDimensions', usableDimensions)
        );
    }

    get pieceDimensionLimit(): ?Dimensions {
        return this._state.get('pieceDimensionLimit');
    }

    setPieceDimensionLimit(pieceDimensionLimit: ?Dimensions): this {
        return this.clone(
            this._state.set('pieceDimensionLimit', pieceDimensionLimit)
        );
    }

    get doorDimensions(): ?DoorDimensions {
        return this._state.get('doorDimensions');
    }

    setDoorDimensions(doorDimensions: ?DoorDimensions): this {
        return this.clone(this._state.set('doorDimensions', doorDimensions));
    }

    get unladenWeight(): ?number {
        return this._state.get('unladenWeight');
    }

    setUnladenWeight(unladenWeight: ?number): this {
        return this.clone(this._state.set('unladenWeight', unladenWeight));
    }

    get maxPayloadWeight(): ?number {
        return this._state.get('maxPayloadWeight');
    }

    setMaxPayloadWeight(maxPayloadWeight: ?number): this {
        return this.clone(
            this._state.set('maxPayloadWeight', maxPayloadWeight)
        );
    }

    get maxStackableWeight(): ?number {
        return this._state.get('maxStackableWeight');
    }

    setMaxStackableWeight(maxStackableWeight: ?number): this {
        return this.clone(
            this._state.set('maxStackableWeight', maxStackableWeight)
        );
    }

    get maxStackableCount(): ?number {
        return this._state.get('maxStackableCount');
    }

    setMaxStackableCount(maxStackableCount: ?number): this {
        return this.clone(
            this._state.set('maxStackableCount', maxStackableCount)
        );
    }

    get limits(): ?RiderPersonaLimits {
        return this._state.get('limits');
    }

    setLimits(limits: ?RiderPersonaLimits): this {
        return this.clone(this._state.set('limits', limits));
    }

    get unloadingSide(): ?LoadingUnloadingSideEnum {
        return this._state.get('unloadingSide');
    }

    setUnloadingSide(unloadingSide: ?LoadingUnloadingSideEnum): this {
        return this.clone(this._state.set('unloadingSide', unloadingSide));
    }

    get transportModes(): Immutable.List<TransportModeEnum> {
        return this._state.get('transportModes');
    }

    setTransportModes(transportModes: Immutable.List<TransportModeEnum>): this {
        return this.clone(this._state.set('transportModes', transportModes));
    }

    get doorCount(): ?number {
        return this._state.get('doorCount');
    }

    setDoorCount(doorCount: ?number): this {
        return this.clone(this._state.set('doorCount', doorCount));
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }
}
